import { useHealthblocksSWR } from '@healthblocks-io/core'
import { useCallback, useEffect } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { DocumentDetailById } from '@/modules/documents/pages/document-detail/DocumentDetailView'
import { useBadgeCount } from '@/modules/notifications/hooks/useBadgeCount'
import { DeeplinkNotFound } from '@/modules/shared/components/deeplink-not-found/DeeplinkNotFound'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { ContentDetail } from '../components/content-detail/ContentDetail'
import { TaskDetailLayout } from '../components/detail-layout/TaskDetailLayout'
import { FileUploadRequestDetail } from '../components/file-upload-request-detail/FileUploadRequestDetail'
import { HylyghtDetail } from '../components/hylyght-detail/HylyghtDetail'
import { MessageDetail } from '../components/message-detail/MessageDetail'
import { QuestionnaireDetail } from '../components/questionnaire-detail/QuestionnaireDetail'
import { RemecareDetail } from '../components/remecare-detail/RemecareDetail'
import { usePendingTaskCount } from '../hooks/usePendingTaskCount'
import { useTrackTaskDetailView } from '../hooks/useTrackTaskDetailView'
import { type Task, TaskStatus } from '../types/task.types'

import type { TaskOutletContext } from './TaskRoot.page'

export const TaskDetailPage = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const { id } = useParams()

  const {
    data: task,
    error,
    mutate,
    isLoading,
  } = useHealthblocksSWR<Task>(`/task/${id}`, { shouldRetryOnError: false })

  useTrackTaskDetailView({ task })

  useHideNativeNavigation()
  const { checkPendingTaskCount } = usePendingTaskCount()
  const { updateBadgeCount } = useBadgeCount()
  const { reloadAndSelectTask } = useOutletContext<TaskOutletContext>()

  const handleRefetch = useCallback(() => {
    mutate()
    reloadAndSelectTask(id!)
  }, [mutate, id, reloadAndSelectTask])

  useEffect(() => {
    if (id) {
      mutate()
      checkPendingTaskCount()
      updateBadgeCount()
    }
  }, [id])

  // Render
  // ------------------------------------------------------------------------- /
  if (isLoading) {
    return (
      <TaskDetailLayout>
        <TaskDetailLayout.ErrorHeader title={''} />
        <TaskDetailLayout.Content>
          <AnimatedLoader />
        </TaskDetailLayout.Content>
      </TaskDetailLayout>
    )
  }

  if (error) {
    return <DeeplinkNotFound origin='task' />
  }

  if (!task) {
    return <DeeplinkNotFound origin='task' />
  }

  if (task.status === TaskStatus.Expired && task.type !== 'Questionnaire') {
    return <DeeplinkNotFound origin='task' />
  }

  switch (task.type) {
    case 'Message': {
      return <MessageDetail task={task} refetch={handleRefetch} />
    }

    case 'Questionnaire': {
      return <QuestionnaireDetail task={task} refetch={handleRefetch} />
    }

    case 'Content': {
      return <ContentDetail task={task} />
    }

    case 'Remecare': {
      return <RemecareDetail task={task} />
    }

    case 'Hylyght': {
      return <HylyghtDetail task={task} refetch={handleRefetch} />
    }

    case 'Document': {
      return <DocumentDetailById id={`documentflow_${task.documentId}`} />
    }

    case 'FileUploadRequest': {
      return <FileUploadRequestDetail task={task} refetch={handleRefetch} />
    }

    default: {
      return <DeeplinkNotFound origin='task' />
    }
  }
}
